import {
  createSelector,
  createSlice,
  nanoid,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from '../../store/store';
import { Flashcard, FlashcardScheduling } from '../../types/Flashcard';
import {
  dueOrderKey,
  localStartOfTomorrow,
} from '../../utils/flashcardOrderJitter';

export type FlashcardRunCardType = 'due' | 'new' | 'none';
export type FlashcardRunQueueKey =
  'priorityDueIds' | 'newQueueIds' | 'deferredDueIds';

/**
 * The user-chosen initial ordering of a run's cards. Purely an ordering
 * preference — it never changes which cards a run contains, the within-queue
 * sort, re-insertion targets, or counts:
 * - 'due'   — priority-due, then new, then deferred-due (default; historical).
 * - 'new'   — new cards first, then priority-due, then deferred-due.
 * - 'mixed' — priority-due and new shuffled together into one stream, with
 *             deferred-due (just-seen cards) kept last, as in the other orders.
 * Mirrors the backend `FlashcardRunOrder` so the two never drift.
 */
export type FlashcardRunOrder = 'due' | 'mixed' | 'new';

export type FlashcardRunScope =
  | {
      kind: 'session';
      studySessionId: string;
      amount?: 'all' | 'count';
      count?: number;
      type?: 'new' | 'due' | 'both' | 'linked';
      order?: FlashcardRunOrder;
    }
  | {
      kind: 'lecture' | 'filters';
      examId?: string;
      topicIds?: string[];
      lectureIds?: string[];
      amount: 'all' | 'count';
      count?: number;
      type: 'new' | 'due' | 'both';
      order?: FlashcardRunOrder;
    }
  /**
   * A study set served in chat (ORA-681). The set fixes both the cards and
   * their order, so it carries no filters of its own: every incomplete item
   * goes into the run in `ord` order.
   */
  | { kind: 'set'; setId: string };

type FlashcardUndoEntry = {
  flashcardId: string;
  previousCard: Flashcard;
  previousSourceQueue: FlashcardRunQueueKey;
  previousSourceIndex: number;
  previousCurrentCardId: string | null;
  previousCurrentCardType: FlashcardRunCardType;
  didIncrementCompletedCount: boolean;
};

export type FlashcardPendingResponse = {
  requestId: string;
  flashcardId: string;
  responseNum: number;
  duration: number | null;
  scheduling: FlashcardScheduling;
  studySessionId?: string;
  flashcardVariantId?: string;
  /** The study set the card was rated in (ORA-681), when the run is one. */
  studySetId?: string;
  attempts: number;
  lastAttemptAt: string | null;
  lastError: string | null;
};

/**
 * The two timestamps the queues order and promote on, kept for every id the
 * run has seen a scheduling for — hydrated or not. `cardsById` is pruned to a
 * few dozen cards, and a queue order that depended on which cards happened to
 * be hydrated collapsed into card-id order in production (ORA-1090).
 */
export type FlashcardSchedulingMeta = { due: string; lastReview: string };

type FlashcardRunState = {
  // External mutations increment this so bootstrap responses that started
  // before the mutation cannot restore a stale run.
  bootstrapGeneration: number;
  scope: FlashcardRunScope | null;
  scopeKey: string | null;
  // Identifies one in-memory run instance. Hydration responses must not write
  // into a later run that happens to use the same scope.
  hydrationRunId: string | null;
  studySessionId: string | null;
  status: 'idle' | 'loading' | 'ready';
  priorityDueIds: string[];
  newQueueIds: string[];
  deferredDueIds: string[];
  completedIds: string[];
  currentCardId: string | null;
  currentCardType: FlashcardRunCardType;
  // Chosen ordering of the run (see FlashcardRunOrder). Drives head selection
  // across the whole run, not just the first card.
  order: FlashcardRunOrder;
  completedCount: number;
  totalCount: number;
  cardsById: Record<string, Flashcard>;
  hydratedCardIds: string[];
  schedulingOverridesByCardId: Record<string, FlashcardScheduling>;
  schedulingMetaById: Record<string, FlashcardSchedulingMeta>;
  undoStack: FlashcardUndoEntry[];
  pendingResponses: FlashcardPendingResponse[];
};

const MAX_UNDO_STACK_SIZE = 20;
const MAX_HYDRATED_CARD_COUNT = 32;

// Exported so a surface that needs its own throwaway run — the in-chat study
// set deck (ORA-687) drives this reducer through `useReducer` — can seed one
// without touching the store the full session uses.
export const initialState: FlashcardRunState = {
  bootstrapGeneration: 0,
  scope: null,
  scopeKey: null,
  hydrationRunId: null,
  studySessionId: null,
  status: 'idle',
  priorityDueIds: [],
  newQueueIds: [],
  deferredDueIds: [],
  completedIds: [],
  currentCardId: null,
  currentCardType: 'none',
  order: 'due',
  completedCount: 0,
  totalCount: 0,
  cardsById: {},
  hydratedCardIds: [],
  schedulingOverridesByCardId: {},
  schedulingMetaById: {},
  undoStack: [],
  pendingResponses: [],
};

const removeId = (ids: string[], flashcardId: string) =>
  ids.filter((id) => id !== flashcardId);

const toIsoString = (value: Date | string): string | null => {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : new Date(time).toISOString();
};

// A run persisted before the map existed can rehydrate without it when the
// persist migration falls back to the raw state. Never let that wedge a rating.
const schedulingMetaOf = (state: FlashcardRunState) => {
  if (!state.schedulingMetaById) {
    state.schedulingMetaById = {};
  }
  return state.schedulingMetaById;
};

// Record the timestamps the queues order and promote on. Independent of
// `cardsById`, which is pruned; a card keeps its entry for the whole run.
const rememberScheduling = (
  state: FlashcardRunState,
  flashcardId: string,
  scheduling: Pick<FlashcardScheduling, 'due' | 'lastReview'> | undefined
) => {
  // A card served without scheduling (a set card before its first rating, a
  // partial replacement) has nothing to remember yet.
  if (!scheduling) return;
  const due = toIsoString(scheduling.due);
  const lastReview = toIsoString(scheduling.lastReview);
  if (due === null || lastReview === null) return;
  schedulingMetaOf(state)[flashcardId] = { due, lastReview };
};

const toTime = (value: Date | string) => new Date(value).getTime();

// The scheduling this client knows to be newer than whatever the server
// returns for the card: an undo's restored scheduling, or a rating whose write
// is still pending. A hydration that lands mid-flight must not roll either
// back — before ORA-1090 a pending rating was overwritten by the stale server
// copy, and that stale due then drove the queues. A pending rating that is
// itself older than the server's copy was overtaken by a write from another
// device, and then the server copy is the truth.
const localSchedulingFor = (
  state: FlashcardRunState,
  flashcardId: string,
  serverScheduling?: FlashcardScheduling
): FlashcardScheduling | undefined => {
  const override = state.schedulingOverridesByCardId[flashcardId];
  if (override) return override;

  const pending = state.pendingResponses.find(
    (entry) => entry.flashcardId === flashcardId
  )?.scheduling;

  if (!pending) return undefined;

  if (
    serverScheduling &&
    toTime(serverScheduling.lastReview) > toTime(pending.lastReview)
  ) {
    return undefined;
  }
  return pending;
};

const applySchedulingOverride = (
  flashcard: Flashcard | undefined,
  override: FlashcardScheduling | undefined
) => {
  if (!flashcard) return null;
  if (!override) return flashcard;
  return {
    ...flashcard,
    scheduling: {
      ...flashcard.scheduling,
      ...override,
    },
  };
};

// FNV-1a 32-bit hash of a card id, used as a stable per-card shuffle key for
// 'mixed' order. Deterministic (so the shuffle survives reloads and re-inserted
// cards keep a consistent slot) and identical to the backend's hash so the two
// never drift.
const flashcardShuffleKey = (id: string): number => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < id.length; index += 1) {
    hash ^= id.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

/**
 * Deferred cards whose own due time has passed, earliest first. These are the
 * run's learning steps: a card rated Again or Hard was told "5m" / "10m", and
 * once that time is up it is served next, whatever the chosen order. Only a
 * card with a known scheduling can be promoted; a deferred card the run has
 * never seen a scheduling for keeps its place at the end.
 */
const overdueDeferredIds = (
  run: {
    deferredDueIds: string[];
    schedulingMetaById?: Record<string, FlashcardSchedulingMeta>;
  },
  now: Date
): string[] => {
  const metaById = run.schedulingMetaById ?? {};
  const nowMs = now.getTime();

  return run.deferredDueIds
    .map((id) => ({ id, dueMs: new Date(metaById[id]?.due ?? NaN).getTime() }))
    .filter((entry) => !Number.isNaN(entry.dueMs) && entry.dueMs <= nowMs)
    .sort((a, b) =>
      a.dueMs !== b.dueMs ? a.dueMs - b.dueMs : a.id.localeCompare(b.id)
    )
    .map((entry) => entry.id);
};

/**
 * The serve order of the run's queues under `order`, as `{ id, type }` entries
 * in the order cards should be presented. This is the single ordering rule for
 * head selection, hydration prefetch, and lookahead, and it mirrors the backend
 * `buildOrderedRunEntries` so the two can never drift.
 *
 * 'mixed' is a stable shuffle of priority-due + new into one stream (keyed on a
 * per-card hash so it is deterministic across recomputes and reloads), with
 * deferred-due kept last so just-seen cards are not resurfaced early.
 *
 * With `now`, deferred cards that are due again lead the stream (see
 * overdueDeferredIds). The backend never passes a clock: at bootstrap a
 * deferred card is by definition not yet due, so the two orders agree.
 */
export const buildFlashcardRunServeEntries = (
  run: {
    priorityDueIds: string[];
    newQueueIds: string[];
    deferredDueIds: string[];
    order: FlashcardRunOrder;
    schedulingMetaById?: Record<string, FlashcardSchedulingMeta>;
  },
  options?: { now?: Date }
): Array<{ id: string; type: 'due' | 'new' }> => {
  const dueEntry = (id: string) => ({ id, type: 'due' as const });
  const newEntry = (id: string) => ({ id, type: 'new' as const });

  const relearningIds = options?.now
    ? overdueDeferredIds(run, options.now)
    : [];

  const relearningSet = new Set(relearningIds);
  const relearning = relearningIds.map(dueEntry);
  const priority = run.priorityDueIds.map(dueEntry);

  const deferred = run.deferredDueIds
    .filter((id) => !relearningSet.has(id))
    .map(dueEntry);

  const news = run.newQueueIds.map(newEntry);

  if (run.order === 'new') {
    return [...relearning, ...news, ...priority, ...deferred];
  }
  if (run.order === 'mixed') {
    // Shuffle priority-due + new; deferred-due (just-seen) stays last.
    const shuffled = [...priority, ...news].sort((a, b) => {
      const keyA = flashcardShuffleKey(a.id);
      const keyB = flashcardShuffleKey(b.id);
      return keyA !== keyB ? keyA - keyB : a.id.localeCompare(b.id);
    });

    return [...relearning, ...shuffled, ...deferred];
  }
  return [...relearning, ...priority, ...news, ...deferred];
};

const recomputeCurrentCard = (
  state: FlashcardRunState,
  avoidFlashcardId?: string
) => {
  const entries = buildFlashcardRunServeEntries(state, { now: new Date() });
  const head = entries[0];

  state.currentCardId = head?.id ?? null;
  state.currentCardType = head?.type ?? 'none';

  if (!avoidFlashcardId || state.currentCardId !== avoidFlashcardId) {
    return;
  }

  // The just-answered card sorted back to the head of its queue; show the
  // next card in serve order instead, unless it is the only card left.
  const alternative = entries.find((entry) => entry.id !== avoidFlashcardId);

  if (!alternative) {
    return;
  }

  state.currentCardId = alternative.id;
  state.currentCardType = alternative.type;
};

const insertIntoQueue = (
  ids: string[],
  flashcardId: string,
  index?: number | null
) => {
  const nextIds = removeId(ids, flashcardId);
  if (typeof index !== 'number' || index < 0 || index > nextIds.length) {
    nextIds.push(flashcardId);
    return nextIds;
  }
  nextIds.splice(index, 0, flashcardId);
  return nextIds;
};

/**
 * Put a just-rated card back into a due queue. The deferred queue is failure
 * order: the card just rated has the newest review, so it goes last and comes
 * back after the cards that were failed before it. The priority queue is the
 * backend's jittered due order, so the card slots in before the first entry
 * known to sort after it; entries without a known scheduling keep their place.
 *
 * Neither path reads `cardsById`, and no comparison ever falls back to the
 * card id. The whole-queue re-sort this replaces did both: once most of a
 * queue had been pruned from `cardsById`, its comparator compared ids, the
 * queue collapsed into id order, and the just-rated card sorted straight back
 * to the head (ORA-1090).
 */
const requeueCard = (
  state: FlashcardRunState,
  flashcardId: string,
  targetQueue: 'priorityDueIds' | 'deferredDueIds'
) => {
  const ids = removeId(state[targetQueue], flashcardId);
  const metaById = schedulingMetaOf(state);
  const meta = metaById[flashcardId];

  if (targetQueue === 'deferredDueIds' || !meta) {
    ids.push(flashcardId);
    state[targetQueue] = ids;
    return;
  }

  const tomorrow = localStartOfTomorrow();

  const key = dueOrderKey({
    cardId: flashcardId,
    due: meta.due,
    lastReview: meta.lastReview,
    tomorrow,
  });

  const index = ids.findIndex((id) => {
    const other = metaById[id];
    if (!other) return false;
    return (
      dueOrderKey({
        cardId: id,
        due: other.due,
        lastReview: other.lastReview,
        tomorrow,
      }) > key
    );
  });

  if (index === -1) {
    ids.push(flashcardId);
  } else {
    ids.splice(index, 0, flashcardId);
  }
  state[targetQueue] = ids;
};

const buildPinnedHydratedIds = (state: FlashcardRunState) => {
  // Pin the head of every queue (not the flat serve order) so the next cards
  // are warm regardless of `order`: whatever the ordering, the upcoming cards
  // are drawn from these queue heads. Ongoing prefetch of cards further ahead is
  // driven order-aware by selectFlashcardRunLookaheadIds.
  const nextIds = [
    ...(state.currentCardId ? [state.currentCardId] : []),
    ...state.priorityDueIds.slice(0, 2),
    ...state.newQueueIds.slice(0, 2),
    ...state.deferredDueIds.slice(0, 2),
    ...state.undoStack.map((entry) => entry.flashcardId),
    ...Object.keys(state.schedulingOverridesByCardId),
    ...state.pendingResponses.map((entry) => entry.flashcardId),
  ];

  return Array.from(new Set(nextIds));
};

const pruneHydratedCards = (state: FlashcardRunState) => {
  const pinnedIds = buildPinnedHydratedIds(state);

  const remainingIds = state.hydratedCardIds.filter(
    (flashcardId) => !pinnedIds.includes(flashcardId)
  );

  state.hydratedCardIds = [...pinnedIds, ...remainingIds].slice(
    0,
    MAX_HYDRATED_CARD_COUNT
  );

  const allowedIds = new Set(state.hydratedCardIds);

  Object.keys(state.cardsById).forEach((flashcardId) => {
    if (!allowedIds.has(flashcardId) && !pinnedIds.includes(flashcardId)) {
      delete state.cardsById[flashcardId];
    }
  });
};

/**
 * Where a card goes after a rating: back into a due queue for this session, or
 * out of the run. Exported because the in-chat deck (ORA-687) asks the same
 * question before it writes — the set item is only marked done on the server
 * when the card has left the queues.
 */
export const queueTargetForScheduling = (
  scheduling: FlashcardScheduling,
  now = new Date()
): 'priorityDueIds' | 'deferredDueIds' | 'completedIds' => {
  const dueDate = new Date(scheduling.due);
  const lastReviewDate = new Date(scheduling.lastReview);
  if (
    scheduling.isSuspended ||
    scheduling.state === 0 ||
    Number.isNaN(dueDate.getTime()) ||
    Number.isNaN(lastReviewDate.getTime())
  ) {
    return 'completedIds';
  }

  const startOfTomorrow = new Date(now);
  startOfTomorrow.setHours(24, 0, 0, 0);
  if (dueDate.getTime() >= startOfTomorrow.getTime()) {
    return 'completedIds';
  }

  const dueNow = dueDate.getTime() <= now.getTime();

  const reviewedRecently =
    now.getTime() - lastReviewDate.getTime() < 8 * 60 * 60 * 1000;

  return dueNow || !reviewedRecently ? 'priorityDueIds' : 'deferredDueIds';
};

/**
 * Ratings still waiting to be written are newer than anything the server
 * handed a fresh run. Apply them to the cards it hydrated, and keep a card out
 * of the queue the pending rating has already moved it from: a graduated card
 * is not served again, and a card mid-step waits in the deferred queue. A
 * pending rating the server has already been overtaken on (another device
 * wrote later) is left alone. Returns whether any queue changed.
 */
const applyPendingRatingsToBootstrap = (state: FlashcardRunState): boolean => {
  const now = new Date();
  let didChangeQueues = false;

  state.pendingResponses.forEach((pending) => {
    const flashcardId = pending.flashcardId;

    const isInRun =
      state.priorityDueIds.includes(flashcardId) ||
      state.newQueueIds.includes(flashcardId) ||
      state.deferredDueIds.includes(flashcardId);

    if (!isInRun) return;

    const hydrated = state.cardsById[flashcardId];
    const local = localSchedulingFor(state, flashcardId, hydrated?.scheduling);
    if (!local) return;

    if (hydrated) {
      state.cardsById[flashcardId] = {
        ...hydrated,
        scheduling: { ...hydrated.scheduling, ...local },
      };
    }
    rememberScheduling(state, flashcardId, local);

    const target = queueTargetForScheduling(local, now);
    if (target === 'completedIds') {
      state.priorityDueIds = removeId(state.priorityDueIds, flashcardId);
      state.newQueueIds = removeId(state.newQueueIds, flashcardId);
      state.deferredDueIds = removeId(state.deferredDueIds, flashcardId);
      if (!state.completedIds.includes(flashcardId)) {
        state.completedIds.push(flashcardId);
        state.completedCount += 1;
      }
      didChangeQueues = true;
      return;
    }

    if (
      target === 'deferredDueIds' &&
      !state.deferredDueIds.includes(flashcardId)
    ) {
      state.priorityDueIds = removeId(state.priorityDueIds, flashcardId);
      state.newQueueIds = removeId(state.newQueueIds, flashcardId);
      state.deferredDueIds.push(flashcardId);
      didChangeQueues = true;
    }
  });

  return didChangeQueues;
};

export type FlashcardRunBootstrapPayload = {
  scope: FlashcardRunScope;
  scopeKey: string;
  studySessionId: string | null;
  priorityDueIds: string[];
  newQueueIds: string[];
  deferredDueIds: string[];
  completedIds?: string[];
  currentCardId: string | null;
  currentCardType: FlashcardRunCardType;
  order?: FlashcardRunOrder;
  completedCount: number;
  totalCount: number;
  hydratedCards: Flashcard[];
};

const flashcardRunSlice = createSlice({
  name: 'flashcardRun',
  initialState,
  reducers: {
    clearFlashcardRun: () => initialState,
    flashcardRunInvalidated: (state) => {
      // Preserve the card currently on screen and any pending rating. Clearing
      // the reuse identity is enough to make the next route entry bootstrap
      // again. The run id deliberately survives so the run already on screen
      // can keep hydrating its remaining cards until that route entry happens.
      state.scopeKey = null;
      state.bootstrapGeneration = (state.bootstrapGeneration ?? 0) + 1;
    },
    setFlashcardRunLoading: (state) => {
      state.status = 'loading';
    },
    flashcardRunBootstrapped: {
      prepare: (payload: FlashcardRunBootstrapPayload) => ({
        payload: { ...payload, hydrationRunId: nanoid() },
      }),
      reducer: (
        state,
        action: PayloadAction<
          FlashcardRunBootstrapPayload & {
            hydrationRunId: string;
          }
        >
      ) => {
        const nextState: FlashcardRunState = {
          ...initialState,
          bootstrapGeneration: state.bootstrapGeneration ?? 0,
          // Ratings not yet written survive a new run: each entry carries its
          // own card, session and scheduling, and the flush reads the store.
          // Dropping them here lost the last rating of a run whenever the
          // next run started before that rating's write had gone out.
          pendingResponses: state.pendingResponses,
          scope: action.payload.scope,
          scopeKey: action.payload.scopeKey,
          hydrationRunId: action.payload.hydrationRunId,
          studySessionId: action.payload.studySessionId,
          status: 'ready',
          priorityDueIds: action.payload.priorityDueIds,
          newQueueIds: action.payload.newQueueIds,
          deferredDueIds: action.payload.deferredDueIds,
          completedIds: action.payload.completedIds ?? [],
          currentCardId: action.payload.currentCardId,
          currentCardType: action.payload.currentCardType,
          // A fresh run adopts the requested order.
          order: action.payload.order ?? 'due',
          completedCount: action.payload.completedCount,
          totalCount: action.payload.totalCount,
          cardsById: Object.fromEntries(
            action.payload.hydratedCards.map((card) => [card.id, card])
          ),
          hydratedCardIds: action.payload.hydratedCards.map((card) => card.id),
          schedulingMetaById: {},
        };

        action.payload.hydratedCards.forEach((card) => {
          rememberScheduling(nextState, card.id, card.scheduling);
        });

        if (applyPendingRatingsToBootstrap(nextState)) {
          recomputeCurrentCard(nextState);
        }

        pruneHydratedCards(nextState);
        return nextState;
      },
    },
    flashcardRunHydrated: (state, action: PayloadAction<Flashcard[]>) => {
      action.payload.forEach((flashcard) => {
        const hydrated = applySchedulingOverride(
          flashcard,
          localSchedulingFor(state, flashcard.id, flashcard.scheduling)
        ) as Flashcard;

        state.cardsById[flashcard.id] = hydrated;
        rememberScheduling(state, flashcard.id, hydrated.scheduling);
      });

      state.hydratedCardIds = Array.from(
        new Set([
          ...action.payload.map((flashcard) => flashcard.id),
          ...state.hydratedCardIds,
        ])
      );

      pruneHydratedCards(state);
    },
    flashcardRunResponseApplied: (
      state,
      action: PayloadAction<{
        requestId: string;
        sourceQueue: FlashcardRunQueueKey;
        updatedCard: Flashcard;
        responseNum: number;
        duration: number | null;
        studySessionId?: string;
        flashcardVariantId?: string;
        studySetId?: string;
      }>
    ) => {
      const { requestId, sourceQueue, updatedCard } = action.payload;
      const previousCard = state.cardsById[updatedCard.id];
      if (!previousCard) {
        return;
      }

      const previousCurrentCardId = state.currentCardId;
      const previousCurrentCardType = state.currentCardType;
      const previousSourceIndex = state[sourceQueue].indexOf(updatedCard.id);
      const targetQueue = queueTargetForScheduling(updatedCard.scheduling);

      state.priorityDueIds = removeId(state.priorityDueIds, updatedCard.id);
      state.newQueueIds = removeId(state.newQueueIds, updatedCard.id);
      state.deferredDueIds = removeId(state.deferredDueIds, updatedCard.id);
      state.completedIds = removeId(state.completedIds, updatedCard.id);

      delete state.schedulingOverridesByCardId[updatedCard.id];
      state.cardsById[updatedCard.id] = updatedCard;
      rememberScheduling(state, updatedCard.id, updatedCard.scheduling);

      const didIncrementCompletedCount = targetQueue === 'completedIds';
      if (targetQueue === 'completedIds') {
        state.completedIds.push(updatedCard.id);
        state.completedCount += 1;
      } else {
        requeueCard(state, updatedCard.id, targetQueue);
      }

      state.undoStack.unshift({
        flashcardId: updatedCard.id,
        previousCard,
        previousSourceQueue: sourceQueue,
        previousSourceIndex,
        previousCurrentCardId,
        previousCurrentCardType,
        didIncrementCompletedCount,
      });

      state.undoStack.splice(MAX_UNDO_STACK_SIZE);

      state.pendingResponses.unshift({
        requestId,
        flashcardId: updatedCard.id,
        responseNum: action.payload.responseNum,
        duration: action.payload.duration,
        scheduling: updatedCard.scheduling,
        studySessionId: action.payload.studySessionId,
        flashcardVariantId: action.payload.flashcardVariantId,
        // Only on the rating that graduates the card. The server marks the set
        // item done on any respond that carries the set id, and a card that is
        // coming back this session is not done yet.
        studySetId: didIncrementCompletedCount
          ? action.payload.studySetId
          : undefined,
        attempts: 0,
        lastAttemptAt: null,
        lastError: null,
      });

      recomputeCurrentCard(state, updatedCard.id);
      pruneHydratedCards(state);
    },
    flashcardRunResponsePersisted: (
      state,
      action: PayloadAction<{ requestId: string }>
    ) => {
      state.pendingResponses = state.pendingResponses.filter(
        (entry) => entry.requestId !== action.payload.requestId
      );

      pruneHydratedCards(state);
    },
    // A rating the server has refused for good (see the flush): it leaves the
    // queue unwritten rather than blocking the card's later ratings forever.
    flashcardRunResponseDropped: (
      state,
      action: PayloadAction<{ requestId: string }>
    ) => {
      state.pendingResponses = state.pendingResponses.filter(
        (entry) => entry.requestId !== action.payload.requestId
      );

      pruneHydratedCards(state);
    },
    flashcardRunResponseFailed: (
      state,
      action: PayloadAction<{ requestId: string; error: string }>
    ) => {
      const pending = state.pendingResponses.find(
        (entry) => entry.requestId === action.payload.requestId
      );

      if (!pending) {
        return;
      }
      pending.attempts += 1;
      pending.lastAttemptAt = new Date().toISOString();
      pending.lastError = action.payload.error;
    },
    flashcardRunPendingAttempted: (
      state,
      action: PayloadAction<{ requestId: string }>
    ) => {
      const pending = state.pendingResponses.find(
        (entry) => entry.requestId === action.payload.requestId
      );

      if (!pending) {
        return;
      }
      pending.attempts += 1;
      pending.lastAttemptAt = new Date().toISOString();
      pending.lastError = null;
    },
    flashcardRunUndoApplied: (state) => {
      const entry = state.undoStack.shift();
      if (!entry) {
        return;
      }

      state.priorityDueIds = removeId(state.priorityDueIds, entry.flashcardId);
      state.newQueueIds = removeId(state.newQueueIds, entry.flashcardId);
      state.deferredDueIds = removeId(state.deferredDueIds, entry.flashcardId);
      state.completedIds = removeId(state.completedIds, entry.flashcardId);

      if (entry.didIncrementCompletedCount) {
        state.completedCount = Math.max(0, state.completedCount - 1);
      }

      state.cardsById[entry.flashcardId] = entry.previousCard;

      state.schedulingOverridesByCardId[entry.flashcardId] =
        entry.previousCard.scheduling;

      rememberScheduling(
        state,
        entry.flashcardId,
        entry.previousCard.scheduling
      );

      state[entry.previousSourceQueue] = insertIntoQueue(
        state[entry.previousSourceQueue],
        entry.flashcardId,
        entry.previousSourceIndex
      );

      state.currentCardId = entry.previousCurrentCardId;
      state.currentCardType = entry.previousCurrentCardType;
      if (!state.currentCardId) {
        recomputeCurrentCard(state);
      }
      pruneHydratedCards(state);
    },
    flashcardRunCardSuspended: (
      state,
      action: PayloadAction<{ flashcardId: string }>
    ) => {
      const { flashcardId } = action.payload;
      state.priorityDueIds = removeId(state.priorityDueIds, flashcardId);
      state.newQueueIds = removeId(state.newQueueIds, flashcardId);
      state.deferredDueIds = removeId(state.deferredDueIds, flashcardId);
      if (!state.completedIds.includes(flashcardId)) {
        state.completedIds.push(flashcardId);
        state.completedCount += 1;
      }
      delete state.schedulingOverridesByCardId[flashcardId];

      state.undoStack = state.undoStack.filter(
        (entry) => entry.flashcardId !== flashcardId
      );

      if (state.cardsById[flashcardId]) {
        state.cardsById[flashcardId] = {
          ...state.cardsById[flashcardId],
          scheduling: {
            ...state.cardsById[flashcardId].scheduling,
            isSuspended: true,
          },
          isSuspended: true,
        };
      }
      recomputeCurrentCard(state);
      pruneHydratedCards(state);
    },
    flashcardRunCardReplaced: (state, action: PayloadAction<Flashcard>) => {
      const replaced = applySchedulingOverride(
        action.payload,
        localSchedulingFor(state, action.payload.id, action.payload.scheduling)
      ) as Flashcard;

      state.cardsById[action.payload.id] = replaced;
      rememberScheduling(state, action.payload.id, replaced.scheduling);

      state.hydratedCardIds = Array.from(
        new Set([action.payload.id, ...state.hydratedCardIds])
      );

      pruneHydratedCards(state);
    },
  },
});

export const selectFlashcardRun = (state: RootState) => state.flashcardRun;

export const selectCurrentRunFlashcard = createSelector(
  [selectFlashcardRun],
  (run) =>
    applySchedulingOverride(
      run.currentCardId ? run.cardsById[run.currentCardId] : undefined,
      run.currentCardId
        ? run.schedulingOverridesByCardId[run.currentCardId]
        : undefined
    )
);

export const selectCurrentRunFlashcardType = createSelector(
  [selectFlashcardRun],
  (run) => run.currentCardType
);

export const selectFlashcardRunHasUndo = createSelector(
  [selectFlashcardRun],
  (run) => run.undoStack.length > 0
);

export const selectFlashcardRunLookaheadIds = createSelector(
  [selectFlashcardRun],
  (run) =>
    Array.from(
      new Set([
        ...(run.currentCardId ? [run.currentCardId] : []),
        // Re-evaluated on every run change, so a card whose step came up since
        // the head was chosen is prefetched before it is served.
        ...buildFlashcardRunServeEntries(run, { now: new Date() }).map(
          (entry) => entry.id
        ),
      ])
    ).slice(0, 3)
);

export const selectFlashcardRunCounts = createSelector(
  [selectFlashcardRun],
  (run) => ({
    newCount: run.newQueueIds.length,
    dueCount: run.priorityDueIds.length + run.deferredDueIds.length,
    completedCount: run.completedCount,
    scheduledCount: 0,
    suspendedCount: 0,
    totalCount: run.totalCount,
  })
);

export const selectSessionFlashcardCompletedCount = createSelector(
  [selectFlashcardRun, (state: RootState) => state.todaySession.session],
  (run, todaySession) => {
    if (
      run.scopeKey &&
      run.studySessionId &&
      run.studySessionId === todaySession?.id
    ) {
      return run.completedCount;
    }
    return todaySession?.completedFlashcardCount ?? 0;
  }
);

export const selectFlashcardRunIsReady = createSelector(
  [selectFlashcardRun],
  (run) => run.status === 'ready'
);

export const {
  flashcardRunInvalidated,
  flashcardRunBootstrapped,
  flashcardRunHydrated,
  flashcardRunResponseApplied,
  flashcardRunResponsePersisted,
  flashcardRunResponseDropped,
  flashcardRunResponseFailed,
  flashcardRunPendingAttempted,
  flashcardRunUndoApplied,
  flashcardRunCardSuspended,
  flashcardRunCardReplaced,
} = flashcardRunSlice.actions;

export default flashcardRunSlice.reducer;
